/* eslint-disable no-console */
import { Auth0Provider } from '@auth0/auth0-react';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { urlJoin } from 'url-join-ts';

import { PaymentPublicClient } from '../backend-client/payment-public-backend';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Loading from '../components/Loading';
import getEnv from '../utils/getEnv';
import { AuthContext } from './authContext';
import { getId, useAuth } from './AuthProvider';
import localAuth0CacheOptions from './localAuth0CacheOptions';
import SingleLoginRedirect from './SingleLoginRedirect';

const {
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_PUBLIC_URL,
  REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
} = getEnv();

// How long to wait for the workflow-version lookup before falling back to the
// unversioned payment-public client. Only the login-hint prefill depends on
// it, so a slow version-controller must never hold up the login itself.
const VERSION_LOOKUP_TIMEOUT_MS = 4000;

/**
 * Note: Auth0 needs explicit access to the history, so we need to get the history
 * with the useHistory hook. Here we're typing Auth0 react-router-dom together.
 *
 * Copied from:
 * https://auth0.com/blog/complete-guide-to-react-user-authentication/
 */
const Auth0ProviderWithHistory: React.FC = ({ children }) => {
  // Use the useLocation hook to access the current location
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated: isAuthAuthenticated, user } = useAuth();

  // The id in a /terms link (path or ?quote=) may be a legacy Quote id or an
  // ApplicationGroup id; getEmailAddress accepts either. This is only used to
  // pre-fill the Auth0 login hint, so no link canonicalization happens here —
  // the Terms page owns the /terms/<applicationGroupId> redirect.
  const inboundTncId =
    new URLSearchParams(location.search).get('quote') || getId('terms');

  const [emailAddress, setEmailAddress] = useState<string>();
  // Start in the loading state whenever there is an id to resolve, so the
  // protected subtree is never mounted against a not-yet-known login hint
  // (NCP-5357 — see the single-pass note on the email effect below).
  const [isLoading, setIsLoading] = useState<boolean>(() => !!inboundTncId);
  const [emailError, setEmailError] = useState<unknown>();
  // `data` is only meaningful once `resolved` is true; the two are held in one
  // piece of state so they can never be observed apart. React 17 does not
  // batch updates made from promise callbacks, so two useStates here would
  // render (and re-run the email effect) twice.
  const [versionLookup, setVersionLookup] = useState<{
    resolved: boolean;
    data?: WorkflowVersion;
  }>({ resolved: false });

  // NCP-4818: all of the state above belongs to ONE inbound id — and so does
  // any per-id state added to this component later, which MUST be reset here
  // too or it will leak across ids exactly the way the login hint did. If the
  // id changes within a single mount, reset it during render — an effect keyed on
  // the id would run only after the stale render COMMITS, so Auth0Provider
  // (and AuthContext below) would expose the previous customer's email as
  // login_hint for one committed frame, and a login redirect fired in that
  // frame would carry the wrong hint to Auth0. A render-phase setState makes
  // React discard this output and re-render with the reset state before
  // anything reaches the DOM, so a stale hint is never committed. Resetting
  // `versionLookup` also keeps the email effect to a single pass per id
  // (NCP-5357): it now waits for the NEW id's version lookup instead of firing
  // immediately against the previous id's resolved version.
  const [prevInboundTncId, setPrevInboundTncId] = useState(inboundTncId);
  if (inboundTncId !== prevInboundTncId) {
    setPrevInboundTncId(inboundTncId);
    setEmailAddress(undefined);
    setEmailError(undefined);
    setVersionLookup({ resolved: false });
    setIsLoading(!!inboundTncId);
  }

  // Resolve the workflow version for the inbound ApplicationGroup id so the
  // email lookup below targets the version-matched payment-public backend
  // (mirrors the version-controller step used elsewhere). Non-fatal: if the id
  // can't be resolved (e.g. a legacy quote-id link) or the lookup is slow, fall
  // back to the unversioned client so the login-hint prefill still works.
  useEffect(() => {
    if (!inboundTncId) {
      return;
    }

    let cancelled = false;
    let settled = false;
    const settle = (data?: WorkflowVersion) => {
      if (cancelled || settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      setVersionLookup({ resolved: true, data });
    };

    // The email fetch now WAITS on this lookup, so without a deadline a hung
    // version-controller request would hold the customer on the loading state
    // instead of falling through to the unversioned client as it used to.
    // (Declared after `settle` because the two reference each other; `settle`
    // only ever runs asynchronously, by which point this is initialised.)
    const timeout = setTimeout(() => {
      console.error(
        'Workflow version lookup timed out for tnc id; using the unversioned payment client'
      );
      settle(undefined);
    }, VERSION_LOOKUP_TIMEOUT_MS);

    new VersionControllerClient(REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL)
      .getVersionFromApplicationGroup(inboundTncId)
      .then((data) => {
        settle(data);
      })
      .catch((err) => {
        console.error('Failed to resolve workflow version for tnc id', err);
        settle(undefined);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [inboundTncId]);

  useEffect(() => {
    if (!inboundTncId || !versionLookup.resolved) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    // This effect runs ONCE per id. It used to run twice — first with the
    // version unresolved (the unversioned fallback) and again once it
    // resolved — and each pass toggled `isLoading`, which swaps the whole
    // subtree below for the loading window. That unmounted and remounted
    // ProtectedRoute -> withAuthenticationRequired, whose mount effect starts
    // an Auth0 `/authorize` navigation: two mounts, two authorize hits, two
    // OTP emails, and Auth0 invalidates the first code the moment it issues
    // the second (NCP-5357). Waiting for the version lookup to settle keeps it
    // to a single pass, so the customer gets exactly one code.
    //
    // `emailError` is still cleared per attempt: it was only ever set, never
    // cleared, so a transient failure survived a later successful attempt and
    // the render below still threw — white-screening /terms for a customer
    // whose email had in fact been fetched fine (NCP-5350).
    setEmailError(undefined);

    new PaymentPublicClient(versionLookup.data?.paymentBackendVersion)
      .getEmailAddress(inboundTncId)
      .then((email) => {
        if (!cancelled) {
          setEmailAddress(email);
          setEmailError(undefined);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setEmailError(err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inboundTncId, versionLookup]);

  const domain = REACT_APP_AUTH0_DOMAIN;
  const clientId = REACT_APP_AUTH0_CLIENT_ID;

  const origin = urlJoin(window.location.origin, REACT_APP_PUBLIC_URL) + '/';

  // Auth0Provider is the STABLE outer wrapper for both the loading window and
  // the loaded app. Previously the loading state returned a bare <div>, so the
  // provider was unmounted and remounted every time `isLoading` toggled. During
  // that window children such as ProtectedRoute -> withAuthenticationRequired
  // invoked Auth0 stub methods (e.g. loginWithRedirect) while no provider was
  // mounted, throwing "You forgot to wrap your component in <Auth0Provider>".
  // Keeping the provider mounted across the toggle prevents that. See NCP-4776.
  //
  // WHY THE `key`: @auth0/auth0-react v1 constructs its Auth0Client exactly
  // once, in a useState initializer on the provider's FIRST mount, and never
  // reacts to later prop changes. But `login_hint` (the customer email) is
  // fetched asynchronously and arrives AFTER that first mount, so a provider
  // mounted once would be frozen with `login_hint: undefined` — the Auth0 page
  // then shows the generic identifier screen with an empty email and never
  // sends the passwordless OTP. Keying on the email makes React recreate the
  // provider (and thus the client) exactly once, when `emailAddress` resolves
  // from undefined to a value, so the client is built WITH the hint. The swap
  // is a single atomic commit — children are never left without a provider, so
  // NCP-4776's "forgot to wrap your component" fix is preserved.
  //
  // SingleLoginRedirect sits directly inside the provider so every consumer of
  // useAuth0() below it gets the guarded loginWithRedirect: at most one Auth0
  // `/authorize` navigation, and therefore one OTP email, per page visit
  // (NCP-5357).
  const withAuth0Provider = (node: React.ReactNode) => (
    <Auth0Provider
      key={emailAddress ?? 'no-login-hint'}
      domain={domain}
      clientId={clientId}
      login_hint={emailAddress}
      redirectUri={origin}
      {...localAuth0CacheOptions}
      onRedirectCallback={(appState) => {
        const revisedReturnTo = appState?.returnTo
          ? appState?.returnTo.replace(`${REACT_APP_PUBLIC_URL}`, '')
          : window.location.pathname.replace(`${REACT_APP_PUBLIC_URL}`, '');
        history.replace(revisedReturnTo || window.location.pathname);
      }}
    >
      <SingleLoginRedirect>{node}</SingleLoginRedirect>
    </Auth0Provider>
  );

  if (isLoading) {
    return withAuth0Provider(
      <div className="grid grid-rows-main h-full w-full text-quartary">
        <Header usedWithRoutes={false} />
        <div
          className="flex"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading"
        >
          <Loading />
        </div>
        <Footer />
      </div>
    );
  }

  if (emailError) {
    throw Error('Failed when trying to get email address');
  }

  // Session-cookie auth path: Auth0 is intentionally not used here.
  if (isAuthAuthenticated) {
    return (
      <AuthContext.Provider value={{ login_hint: user?.email }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return withAuth0Provider(
    <AuthContext.Provider value={{ login_hint: emailAddress }}>
      {children}
    </AuthContext.Provider>
  );
};

export default Auth0ProviderWithHistory;
